/**
 * @fileoverview Utility function for normalizing Company data to CompanyItem format
 * @active Used in current Next.js App Router implementation for companies list
 * 
 * This function transforms a raw Company object (from the API) into a CompanyItem object,
 * which is the format expected by the NewCompaniesList and NewCompaniesCards components.
 * 
 * It replaces the deprecated companyFieldMatching function from the legacy implementation.
 * 
 * @see {@link src/components/companies/companiesList/newCompaniesList/types.ts} - CompanyItem type definition
 * @see {@link src/components/companies/NewCompanies.tsx} - Component using Company and CompanyItem types
 * @see {@link src/components/companies/companiesList/dataStore/correspondingFieldToHeader.jsx} - Deprecated legacy implementation
 * @see {@link src/utils/formatDate.ts} - Date formatting utilities
 * @see {@link src/utils/formatNumber.ts} - Format number utilities
 * @see {@link src/utils/imgSelector.ts} - Image selector utilities
*/

import type { Company } from "@/types/company";
import { formatDateTimeFromDB, formatValues } from "@/utils/formatDate";
import { formatNumber } from "@/utils/formatNumber";
import { imgSelector } from "@/utils/imgSelector";

/**
 * Normalizes a Company object to CompanyItem format
 * 
 * @param {Company} company - Raw Company object from the API
 * @returns {Company} - Normalized Company object
 */
export const normalizeCompanyData = (company: Company): Company => {
  const {
    id,
    slug,
    dateYearEstablished,
    fileLogo,
    nameBrand,
    statusFeatured,
    statusHidePage,
    statusOperating,
    statusSitemapShow,
    statusVerified,
    typeRecord,
    sourceId,
    nameLegal,
    createdAt,
    updatedAt,
    webUrl,
    hasFundraiseTrans,
    activeRound,
    stage,
    totalRaised,
    fundraises,
    details,
    accelerator,
    descriptionExpanded,
    descriptionShort,
    descriptionEdited,
    mainAddress,
    addresses,
    typeOwnership,
    typeCustomers,
    mainProducts,
    typeModel,
    typeRevenue,
    geoLocationExposure,
    tags,
    typeTechnologyUsed,
    company: companyDetails,
    description,
    links,
    headQuarters,
    raisedInRound,
    pitchdeck,
    currency,
    lastUpdate,
    ...rest
  } = company;

  return {
    id,
    slug,
    dateYearEstablished,
    fileLogo: fileLogo ? imgSelector({ data: { fileLogo, fileLogoSquare: company.fileLogoSquare, fileLogoRectangle: company.fileLogoRectangle }, imgType: 'company' }) : undefined,
    nameBrand,
    statusFeatured,
    statusHidePage,
    statusOperating,
    statusSitemapShow,
    statusVerified,
    typeRecord,
    sourceId,
    nameLegal,
    createdAt,
    updatedAt,
    webUrl,
    hasFundraiseTrans,
    activeRound,
    stage,
    totalRaised,
    fundraises,
    details,
    accelerator,
    descriptionExpanded,
    descriptionShort,
    descriptionEdited,
    mainAddress,
    addresses,
    typeOwnership,
    typeCustomers,
    mainProducts,
    typeModel,
    typeRevenue,
    geoLocationExposure,
    tags,
    typeTechnologyUsed,
    company: {
      companyName: companyDetails.companyName,
      fileLogo: companyDetails.fileLogo ?? undefined,
      slug: companyDetails.slug,
    },
    description,
    links,
    headQuarters,
    raisedInRound: raisedInRound ? formatNumber(typeof activeRound === 'string' ? 0 : activeRound.amountRaised ?? 0) : undefined,
    pitchdeck,
    currency,
    lastUpdate: lastUpdate ? formatDateTimeFromDB(updatedAt, formatValues.numeric) : undefined,
    ...rest,
  };
};
